import { useStripe } from '@stripe/react-stripe-js';
import { FormikHelpers, useFormik } from 'formik';
import { get } from 'lodash';
import React, { useContext } from 'react';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { mixed, object, ref } from 'yup';

import apiUtil from 'src/api/payments';
import CustomToast from 'src/components/CustomToast';
import FormikSubmit from 'src/components/FormikSubmit';
import { AppContext } from 'src/containers/Core/AppContext';
import { AppState } from 'src/containers/Core/AppReducer';
import { requiredSchema } from 'src/validationSchemas';

import './style.scss';

type BankAccountFormValues = {
  accountNumber: string;
  confirmAccountNumber: string;
  accountHolderName: string;
  routingNumber: string;
};

type BankAccountFormProps = {
  onSuccess: (response: BankDetails) => void;
};

enum StripeBankAccountErrorCode {
  INVALID_ROUTING_NUMBER = 'routing_number_invalid',
  INVALID_ACCOUNT_NUMBER = 'account_number_invalid',
  INVALID_ACCOUNT_HOLDER_NAME = 'account_holder_name_invalid',
}

const BankAccountForm: React.FunctionComponent<BankAccountFormProps> = ({ onSuccess }) => {
  const { store } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;
  const businessUuid = get(businessOnboardingDetails, 'business.uuid');
  const stripe = useStripe();

  const initialValues: BankAccountFormValues = {
    accountNumber: '',
    confirmAccountNumber: '',
    accountHolderName: '',
    routingNumber: '',
  };

  const validationSchema = object({
    accountNumber: requiredSchema,
    confirmAccountNumber: mixed()
      .oneOf([ref('accountNumber')], 'Account number must match')
      .required('Required'),
    accountHolderName: requiredSchema,
    routingNumber: requiredSchema,
  });

  const handleBankFormSubmit = async (data: BankAccountFormValues, actions: FormikHelpers<BankAccountFormValues>) => {
    if (!stripe) {
      return;
    }

    try {
      //@ts-ignore
      const { error, token } = await stripe.createToken('bank_account', {
        country: 'US',
        currency: 'usd',
        account_holder_type: 'company',
        account_number: data.accountNumber,
        account_holder_name: data.accountHolderName,
        routing_number: data.routingNumber,
      });

      if (error) {
        switch (error.code) {
          case StripeBankAccountErrorCode.INVALID_ACCOUNT_NUMBER:
            actions.setErrors({ accountNumber: 'Account Number is invalid' });

            return;

          case StripeBankAccountErrorCode.INVALID_ACCOUNT_HOLDER_NAME:
            actions.setErrors({ accountHolderName: error.message });

            return;

          case StripeBankAccountErrorCode.INVALID_ROUTING_NUMBER:
            actions.setErrors({ routingNumber: error.message });

            return;
        }
      }

      const response = await apiUtil.createBankAccount(businessUuid, token.id, true);
      onSuccess(response);
    } catch (error) {
      toast(<CustomToast description={get(error, 'response.data.error')} />);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleBankFormSubmit,
  });

  const { handleSubmit, values, errors, touched, handleChange, isSubmitting } = formik;

  const setAttributes = (type: 'accountNumber' | 'confirmAccountNumber') => ({
    className: 'font-size-14',
    name: type,
    value: values[type],
    onChange: handleChange,
    isInvalid: touched[type] && !!errors[type],
    disabled: isSubmitting,
    maxLength: 255,
  });

  return (
    <Form noValidate className="w-50" onSubmit={handleSubmit} key={'account-form'}>
      <Form.Group className="mb-2" controlId="AccountHolderName">
        <Form.Label id="accountHolderName" className="font-size-14 required mb-0">
          Account Holder Name
        </Form.Label>
        <Form.Control
          autoFocus={true}
          className="font-size-14"
          value={values.accountHolderName}
          type="text"
          placeholder="Enter account holder name"
          onChange={handleChange}
          isInvalid={touched.accountHolderName && !!errors.accountHolderName}
          autoComplete="off"
          name="accountHolderName"
          disabled={isSubmitting}
          maxLength={255}
        />
        <Form.Control.Feedback type="invalid">{errors.accountHolderName}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-2" controlId="AccountNumber">
        <Form.Label id="accountNumber" className="font-size-14 required mb-0">
          Account Number
        </Form.Label>
        <Form.Control
          type="password"
          placeholder="Enter account number"
          autoComplete="new-password"
          {...setAttributes('accountNumber')}
        />
        <Form.Control.Feedback type="invalid">{errors.accountNumber}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-2" controlId="ConfirmAccountNumber">
        <Form.Label id="confirmAccountNumber" className="font-size-14 required mb-0">
          Re-enter Account Number
        </Form.Label>
        <Form.Control
          value={values.confirmAccountNumber}
          type="text"
          placeholder="Confirm account number"
          autoComplete="new-password"
          {...setAttributes('confirmAccountNumber')}
        />
        <Form.Control.Feedback type="invalid">{errors.confirmAccountNumber}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-2" controlId="RoutingNumber">
        <Form.Label id="routingNumber" className="font-size-14 required mb-0">
          Routing Number
        </Form.Label>
        <Form.Control
          className="font-size-14"
          value={values.routingNumber}
          type="text"
          placeholder="Enter routing number"
          onChange={handleChange}
          isInvalid={touched.routingNumber && !!errors.routingNumber}
          autoComplete="off"
          name="routingNumber"
          disabled={isSubmitting}
          maxLength={255}
        />
        <Form.Control.Feedback type="invalid">{errors.routingNumber}</Form.Control.Feedback>
      </Form.Group>
      <FormikSubmit size="sm" className="mt-1 mb-3 py-2" loading={isSubmitting} text="Add Bank Account" />
    </Form>
  );
};

export default BankAccountForm;
